import ControllerBase from '../../../../global/js/ControllerBase';
import { OrganismShoppingCartEvents } from './o-shopping-cart.events';
import { CartResponse, CartItemUpdate } from './o-shopping-cart.model';
import { AtomQuantitySelectorEvents } from '../../atoms/a-quantity-selector/a-quantity-selector.events';
import UIkit from 'uikit';
import { OrganismShoppingCartProps } from './o-shopping-cart.data';
import CartFetcher from './o-shopping-cart-fetcher';
import ControllerProxy from '../../../../global/js/ControllerProxy';
import Controller from '../../../global/js/controller';

const innerCartTemplate = require('components/organisms/o-shopping-cart/o-shopping-cart.inner.template.twig') as any;

export default class ControllerShoppingCart extends ControllerBase {
	private readonly componentName = 'o-shopping-cart';

	/** A list of dom element references available to this component */
	private readonly componentRefs = {
		totalPrice: 'totalPrice',
		outerCart: 'outerCart',
	};

	private cartFetcher: CartFetcher;
	private showOnInit: boolean;
	private updateCartOnInit: boolean;

	constructor(element: HTMLElement) {
		super(element, ['subtractQuantity', 'addQuantity', 'quantity', 'productId', 'quantityLabel']);

		const cartUrl = <string>element.dataset.carturl;
		const addToCartUrl = <string>element.dataset.addtocarturl;
		const updateCartItemUrl = <string>element.dataset.updatecartitemurl;
		this.cartFetcher = new CartFetcher(cartUrl, addToCartUrl, updateCartItemUrl);
		this.showOnInit = element.dataset.showoninit === 'true';
		this.updateCartOnInit = false;

		this.registerEventListeners();

		if (this.updateCartOnInit) {
			this.getCart().then((response: OrganismShoppingCartProps) => {
				this.renderInnerCart(response);
			});
		}

		if (this.showOnInit) {
			this.showCart();
		}
	}

	private registerEventListeners(): void {
		this.observeCartChanges();
	}

	private showCart(): void {
		UIkit.offcanvas(this.$element).show();
	}

	private toggleSpinner(): void {
		this.$element.classList.toggle(this.componentName + '--loading');
	}

	private observeCartChanges(): void {
		this.$element.addEventListener(OrganismShoppingCartEvents.postRender, this.onPostRender.bind(this));
		this.$element.addEventListener(AtomQuantitySelectorEvents.quantityChangeEvent, this.onUpdateCartItem.bind(this));
		window.addEventListener(OrganismShoppingCartEvents.addToCart, this.onAddToCart.bind(this));
	}

	private async onUpdateCartItem(e: CustomEvent<CartItemUpdate>) {
		this.updateCartItem(e.detail).then((cartResponse: CartResponse) => {
			this.renderInnerCart(cartResponse);
		});
	}

	private async onAddToCart(e: CustomEvent<CartItemUpdate>) {
		this.addToCart(e.detail).then((cartResponse: CartResponse) => {
			this.renderInnerCart(cartResponse);
		});
	}

	private async onPostRender(e: CustomEvent<CartResponse>) {
		this.showCart();
		this.toggleSpinner();
		console.info('cart rendered');
	}

	/**
	 * Update the given cartItem
	 * @param cartItemUpdate
	 * @private
	 */
	private async updateCartItem(cartItemUpdate: CartItemUpdate): Promise<OrganismShoppingCartProps> {
		this.toggleSpinner();
		return this.cartFetcher.updateCartItem(cartItemUpdate);
	}

	/**
	 * Get the current cart data
	 * @private
	 */
	private async getCart(): Promise<OrganismShoppingCartProps> {
		this.toggleSpinner();
		return this.cartFetcher.getCart();
	}

	/**
	 * Add an item to the cart with the given quantity
	 * @param cartItemUpdate
	 * @private
	 */
	private addToCart(cartItemUpdate: CartItemUpdate): Promise<OrganismShoppingCartProps> {
		this.toggleSpinner();
		return this.cartFetcher.addCartItem(cartItemUpdate);
	}

	/**
	 * Render the inner cart template based on the given cartResponse
	 * @param cartResponse
	 * @private
	 */
	private renderInnerCart(cartResponse: CartResponse): void {
		this.domUtils.renderDomElement(
			this.componentRefs.outerCart,
			innerCartTemplate({
				config: { name: 'o-shopping-cart' },
				data: cartResponse,
			}),
		);
		this.initializeNestedComponents();
		this.dispatchCustomEvent(OrganismShoppingCartEvents.postRender, null, true);
	}

	protected initializeNestedComponents(): void {
		const components = this.$element.querySelectorAll('[data-controller]');
		components.forEach((element: HTMLElement) => {
			const componentName = <string>element.dataset.controller;
			new ControllerProxy(Controller, componentName, element);
		});
	}
}
